import React, { ChangeEvent, useEffect, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { autocompleteAirports } from "../../../api";
import { selectAppConfig, selectConfig } from "../../config/ConfigSlice";
import { AirportOption, RMLocation } from "../../../types";
import { getAirportLabel, getAirportSuggestionLabel } from "../../../utils";
import { useCombobox } from "downshift";
import { useTranslation } from "react-i18next";
import { IconDefinition } from "@fortawesome/free-solid-svg-icons";
import { changeStartAirport } from "../searchFlightsSlice";

interface AirportInputProps {
  id: string;
  airport: RMLocation;
  validity: boolean;
  validityMessage: string;
  onSelect: (airport: RMLocation) => void;
  icon: IconDefinition;
  label: string;
  placeholder?: string;
  title: string;
  disabled: boolean;
  excludeAirport?: RMLocation;
}

interface AirportDropdownProps {
  id: string;
  code: string;
  onSelect: (airport: RMLocation) => void;
  icon: IconDefinition;
  options: AirportOption[];
  label: string;
}

interface AirportAutocompleteProps {
  id: string;
  airport: RMLocation;
  validity: boolean;
  validityMessage: string;
  onSelect: (airport: RMLocation) => void;
  icon: IconDefinition;
  label: string;
  placeholder: string;
  disabled: boolean;
  title: string;
  excludeAirport?: RMLocation;
}

function AirportInput({
  id,
  airport,
  validity,
  validityMessage,
  onSelect,
  icon,
  label,
  placeholder = "",
  title,
  disabled = false,
  excludeAirport,
}: AirportInputProps) {
  const multi = useAppSelector(
    (state) => state.config.app.customerType.airportVersion.multi
  );
  const airports = useAppSelector((state) => state.searchFlights.startAirports);

  if (multi && disabled) {
    return (
      <AirportDropdown
        id={id}
        code={airport?.port.code}
        onSelect={onSelect}
        icon={icon}
        options={airports}
        label={label}
      />
    );
  } else {
    return (
      <AirportAutocomplete
        id={id}
        airport={airport}
        validity={validity}
        validityMessage={validityMessage}
        onSelect={onSelect}
        icon={icon}
        label={label}
        placeholder={placeholder}
        disabled={disabled}
        title={getAirportLabel(airport)}
        excludeAirport={excludeAirport}
      />
    );
  }
}

function AirportDropdown({
  id,
  code,
  onSelect,
  icon,
  options,
  label,
}: AirportDropdownProps) {
  const dispatch = useAppDispatch();
  const { mapper, app } = useAppSelector(selectConfig);

  const onCodeSelect = (e: ChangeEvent<HTMLSelectElement>) => {
    const code = e.target.value;
    dispatch(changeStartAirport(code));
    autocompleteAirports(app, mapper, code)
      .then((json) => {
        const airport = json.results.find(
          (airport) => airport.port.code === code
        );
        if (airport) {
          onSelect({ ...airport, readOnly: true });
        }
      })
      .catch(() => {});
  };

  return (
    <div className="field is-horizontal">
      <div className="field-label is-normal">
        <label className="label" htmlFor={id}>
          {label}
        </label>
      </div>
      <div className="field-body">
        <div className="field">
          <div className="control has-icons-left">
            <div className="select is-fullwidth">
              <select id={id} value={code} onChange={onCodeSelect}>
                {options.map((airport, i) => (
                  <option key={i} value={airport.code}>
                    {airport.label}
                  </option>
                ))}
              </select>
              <span className="icon is-left">
                <FontAwesomeIcon icon={icon} />
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function AirportAutocomplete({
  id,
  airport,
  validity,
  validityMessage,
  onSelect,
  icon,
  label,
  placeholder = "",
  disabled,
  title,
  excludeAirport,
}: AirportAutocompleteProps) {
  const { t, i18n } = useTranslation();
  const dropdownRef = useRef(null);
  const [airportSuggestions, setAirportSuggestions] = React.useState<
    RMLocation[]
  >([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);
  const [minLength, setMinLength] = useState<number>(3);
  const { mapper, app } = useAppSelector(selectConfig);
  const { airportsFilter, airportsFilterOut, airportMarkerFilter, customerId } =
    useAppSelector(selectAppConfig);
  const directMarkers = useAppSelector((state) => state.marker.direct.markers);
  const connectionMarkers = useAppSelector(
    (state) => state.marker.connection.markers
  );
  const allowSameDestination = useAppSelector(
    (state) => state.config.mapper.allowSameDestination
  );

  const markerCodes = [
    ...directMarkers.map((marker) => marker.code),
    ...connectionMarkers.map((marker) => marker.code),
  ];

  useEffect(() => {
    const langCode = i18n.language?.substring(0, 2).toLowerCase();
    if (langCode === "zh" || langCode === "ja") {
      setMinLength(2);
    }
  }, [i18n.language]);

  const onInputValueChange = ({ inputValue, type }) => {
    if (
      type === useCombobox.stateChangeTypes.InputChange &&
      inputValue !== null &&
      inputValue.length >= minLength
    ) {
      setLoading(true);
      setError(false);
      autocompleteAirports(app, mapper, inputValue)
        .then((json) => {
          const filteredResults = json.results
            .filter(
              (result) =>
                customerId === "AUS" ||
                airportsFilter.length === 0 ||
                airportsFilter.includes(result.port.code)
            )
            .filter((result) => !airportsFilterOut.includes(result.port.code))
            .filter((result) => {
              return (
                !airportMarkerFilter ||
                markerCodes.length === 0 ||
                markerCodes.includes(result.port.code)
              );
            })
            .filter((result) => {
              return (
                !excludeAirport || result.port.code !== excludeAirport.port.code
              );
            })
            .filter((result) => {
              return (
                allowSameDestination ||
                result.port.code !== excludeAirport?.port.code
              );
            });
          const suggestions = [];
          for (const result of filteredResults) {
            if (result.port.type === "M") {
              result.port.translations[
                Object.keys(result.port.translations)[0]
              ] = t("ALL_AIRPORTS");
            }
            suggestions.push(result);
            if (
              result.port.code === inputValue.toUpperCase() &&
              result.port.type === "A" &&
              filteredResults.length > 1
            ) {
              suggestions.push(null);
            }
          }
          setAirportSuggestions(suggestions);
          setLoading(false);
        })
        .catch(() => {
          setLoading(false);
          setError(true);
          setAirportSuggestions([]);
        });
    }
  };

  const {
    inputValue,
    setInputValue,
    getLabelProps,
    getMenuProps,
    getInputProps,
    getItemProps,
    getComboboxProps,
    isOpen,
    highlightedIndex,
  } = useCombobox({
    inputId: id,
    items: airportSuggestions,
    itemToString: (item) => {
      return getAirportSuggestionLabel(item);
    },
    onSelectedItemChange: ({ selectedItem }) => {
      onSelect({ ...selectedItem, readOnly: false });
    },
    onInputValueChange: onInputValueChange,
  });

  useEffect(() => {
    if (airport) {
      setInputValue(getAirportSuggestionLabel(airport));
    } else {
      setInputValue("");
    }
  }, [airport, setInputValue]);

  return (
    <div className="field is-horizontal">
      <div className="field-label is-normal">
        <label className="label" {...getLabelProps()} htmlFor={id}>
          {label}
        </label>
      </div>
      <div className="field-body">
        <div className="field">
          <div
            className={`control has-icons-left${loading ? " is-loading" : ""}`}
          >
            <div
              ref={dropdownRef}
              className={`dropdown${
                isOpen && inputValue.length >= minLength ? " is-active" : ""
              }`}
              style={{ width: "100%" }}
            >
              <div
                className="dropdown-trigger"
                {...getComboboxProps()}
                style={{ width: "100%" }}
              >
                <div>
                  <input
                    className={`input ${validity === false ? "is-danger" : ""}`}
                    id={id}
                    placeholder={placeholder}
                    {...getInputProps()}
                    disabled={disabled}
                    title={title}
                  />
                  <span className="icon is-left">
                    <FontAwesomeIcon icon={icon} />
                  </span>
                </div>
              </div>
              <div
                className="dropdown-menu airport-suggestions-dropdown-menu"
                {...getMenuProps()}
                role="menu"
              >
                <div className="dropdown-content">
                  {loading && (
                    <div className="dropdown-item">
                      <div className="mini-loader-wrapper">
                        <div className="loader is-loading" />
                      </div>
                    </div>
                  )}
                  {error && (
                    <div className="dropdown-item">
                      {t("ERROR_CANNOT_RETRIEVE_RESULTS")}
                    </div>
                  )}
                  {!error && !loading && (
                    <>
                      {airportSuggestions.length === 0 && (
                        <div className="dropdown-item">
                          {t("NO_RESULTS_FOUND")}
                        </div>
                      )}
                      {airportSuggestions.map((item, index) => {
                        if (item === null) {
                          return (
                            <hr key={index} className="dropdown-divider" />
                          );
                        }
                        const isFocused = isOpen && index === highlightedIndex;
                        return (
                          <a
                            key={index}
                            {...getItemProps({ item, index })}
                            className={`dropdown-item airport-suggestion-item${
                              isFocused ? " focused" : ""
                            }`}
                          >
                            <div className="columns is-mobile is-vcentered">
                              <div
                                className="column airport-suggestion-item-label"
                                title={getAirportLabel(item)}
                              >
                                {getAirportLabel(item)}
                              </div>
                              <div className="column is-narrow has-text-right">
                                {item.port.type === "M" ? null : (
                                  <span className="tag is-family-monospace">
                                    {item.port.code}
                                  </span>
                                )}
                              </div>
                            </div>
                          </a>
                        );
                      })}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
          {validity === false && (
            <p className="help is-danger">{validityMessage}</p>
          )}
        </div>
      </div>
    </div>
  );
}

export default AirportInput;
